import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppHeader from '../components/AppHeader';
import { getPayment } from '../api/paymentsApi';
import LoadingSpinner from '../components/LoadingSpinner';

function PaymentDetail() {
  const { id } = useParams();
  const { t } = useTranslation();

  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchPayment() {
      try {
        const result = await getPayment(id);
        if (result.success) {
          setPayment(result.payment);
        } else {
          setError(result.message || 'Failed to load payment.');
        }
      } catch (err) {
        setError('Could not connect to server.');
      } finally {
        setLoading(false);
      }
    }

    fetchPayment();
  }, [id]);

  if (loading) {
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <AppHeader title={t('paymentDetail.title')} showBack={true} fallbackRoute="/payments" />
      <LoadingSpinner fullScreen />
    </div>
  );
}

  if (error || !payment) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
        <AppHeader title="Error" showBack={true} fallbackRoute="/payments" />
        <div style={{ padding: 16, color: '#dc2626' }}>{error || 'Payment not found.'}</div>
      </div>
    );
  }

  const isPaid = payment.pay_status === 'paid';
  const createdDateOnly = payment.created_at ? payment.created_at.split(' ')[0] : '—';

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <AppHeader title={t('paymentDetail.title')} showBack={true} fallbackRoute="/payments" />

      <div style={{ padding: 16 }}>
        <div style={styles.card}>
          <div style={styles.topRow}>
            <span style={styles.loadLabel}>{t('paymentDetail.load')} #{payment.load_id}</span>
            <span style={{
              ...styles.statusPill,
              backgroundColor: isPaid ? '#d1fae5' : '#fef3c7',
              color: isPaid ? '#065f46' : '#92400e',
            }}>
              {isPaid ? t('payments.paid') : t('payments.notPaid')}
            </span>
          </div>

          <div style={styles.amount}>
            {Number(payment.amount_value).toLocaleString()} {payment.currency}
          </div>

          <div style={styles.divider} />

          <div style={styles.row}>
            <span style={styles.rowLabel}>{t('paymentDetail.rate')}</span>
            <span style={styles.rowValue}>{payment.rate_percentage ?? '—'}%</span>
          </div>
          <div style={styles.row}>
            <span style={styles.rowLabel}>{t('paymentDetail.createdDate')}</span>
            <span style={styles.rowValue}>{createdDateOnly}</span>
          </div>
          <div style={styles.row}>
            <span style={styles.rowLabel}>{t('paymentDetail.paymentDate')}</span>
            <span style={styles.rowValue}>
              {payment.payment_date || t('paymentDetail.notPaidYet')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  card: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 18,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  loadLabel: {
    fontSize: 15,
    fontWeight: 700,
    color: '#111827',
  },
  statusPill: {
    fontSize: 11,
    fontWeight: 700,
    padding: '4px 12px',
    borderRadius: 999,
  },
  amount: {
    fontSize: 28,
    fontWeight: 800,
    color: '#2563eb',
    marginBottom: 16,
  },
  divider: {
    height: 1,
    backgroundColor: '#f3f4f6',
    marginBottom: 12,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingVertical: 8,
    marginBottom: 8,
  },
  rowLabel: {
    fontSize: 13,
    color: '#6b7280',
  },
  rowValue: {
    fontSize: 13,
    fontWeight: 600,
    color: '#111827',
  },
};

export default PaymentDetail;